/**
 * Compatibility layer for React Router hooks using Next.js router
 * This allows existing components to work without major refactoring
 */

import { useRouter } from 'next/router';
import { useMemo } from 'react';

/**
 * Compatibility hook for useLocation from react-router-dom
 */
export const useLocation = () => {
  const router = useRouter();
  
  return useMemo(() => {
    // Handle SSR or router not ready yet
    if (!router || !router.isReady) {
      return {
        pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
        search: typeof window !== 'undefined' ? window.location.search : '',
        hash: typeof window !== 'undefined' ? window.location.hash : '',
        state: null,
      };
    }
    
    return {
      pathname: router.pathname,
      search: typeof window !== 'undefined' ? window.location.search : '',
      hash: typeof window !== 'undefined' ? window.location.hash : '',
      state: null,
    };
  }, [router, router?.pathname, router?.isReady]);
};

/**
 * Compatibility hook for useHistory from react-router-dom
 */
export const useHistory = () => {
  const router = useRouter();
  
  return useMemo(() => {
    // Handle SSR or router not ready yet
    const pathname = router?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
    
    return {
      push: (path: string) => router?.push(path),
      replace: (path: string) => router?.replace(path),
      goBack: () => router?.back(),
      goForward: () => {
        if (typeof window !== 'undefined') {
          window.history.forward();
        }
      },
      length: typeof window !== 'undefined' ? window.history.length : 0,
      location: {
        pathname,
        search: typeof window !== 'undefined' ? window.location.search : '',
        hash: typeof window !== 'undefined' ? window.location.hash : '',
        state: null,
      },
    };
  }, [router, router?.pathname, router?.isReady]);
};

/**
 * Compatibility hook for useParams from react-router-dom
 */
export const useParams = <T = Record<string, string>>(): T => {
  const router = useRouter();
  return (router?.query || {}) as T;
}; 